import {useState} from "react";
import {useParams, useHistory} from "react-router-dom";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {Alert, Button, Chip, CircularProgress, FormControl, InputLabel, MenuItem, Select, TextField} from "@mui/material";
import {useAdminApi, ADMIN_USERS_KEY, ADMIN_ADD_USER_OPTIONS_KEY} from "./api";

export function UserDetails() {
    var {id} = useParams<{id: string}>();
    var history = useHistory();
    var api = useAdminApi();
    var queryClient = useQueryClient();

    var {data: users, isLoading: loadingUsers} = useQuery(
        [ADMIN_USERS_KEY],
        () => api.listUsers(),
        {staleTime: 30000}
    );

    var {data: options} = useQuery(
        [ADMIN_ADD_USER_OPTIONS_KEY],
        () => api.getAddUserOptions(),
        {staleTime: 60000}
    );

    var user = users?.find(u => u.id.toString() === id);

    var [firstName, setFirstName] = useState<string | null>(null);
    var [lastName, setLastName] = useState<string | null>(null);
    var [roles, setRoles] = useState<string[] | null>(null);
    var [saving, setSaving] = useState(false);
    var [error, setError] = useState<string | null>(null);

    if (loadingUsers) {
        return <div style={{display: "flex", justifyContent: "center", padding: 32}}><CircularProgress/></div>;
    }

    if (!user) {
        return <Alert severity="error" sx={{m: 2}}>User "{id}" not found</Alert>;
    }

    var editFirstName = firstName ?? user.firstName;
    var editLastName = lastName ?? user.lastName;
    var editRoles = roles ?? user.roles ?? [];

    async function handleSave() {
        if (!user) return;
        try {
            setSaving(true);
            setError(null);
            await api.updateUser(user.id, editFirstName, editLastName);
            await api.updateUserRoles(user.id, editRoles);
            await queryClient.invalidateQueries([ADMIN_USERS_KEY]);
            history.push("/admin/users");
        } catch (e: any) {
            setError(e.message || "Failed to update user");
        } finally {
            setSaving(false);
        }
    }

    return (
        <div>
            <Button startIcon={<i className="fas fa-arrow-left"/>} onClick={() => history.push("/admin/users")} sx={{mb: 2}}>
                Back
            </Button>

            <h2>User: {user.email}</h2>

            <div style={{marginBottom: 16, display: "flex", gap: 8}}>
                <Chip
                    label={`Namespace: ${user.namespace}`}
                    icon={<i className="fas fa-layer-group" style={{fontSize: 14}}/>}
                    clickable
                    onClick={() => history.push(`/admin/namespaces/${user.namespace}`)}
                />
                <Chip
                    label={`Account: ${user.accountKey}`}
                    icon={<i className="fas fa-building" style={{fontSize: 14}}/>}
                    clickable
                    onClick={() => history.push(`/admin/accounts/${user.accountKey}`)}
                />
            </div>

            {error && <Alert severity="error" sx={{mb: 2}}>{error}</Alert>}

            <div style={{maxWidth: 400, marginBottom: 24}}>
                <TextField
                    margin="dense"
                    label="First Name"
                    fullWidth
                    variant="outlined"
                    value={editFirstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    sx={{mb: 2}}
                />

                <TextField
                    margin="dense"
                    label="Last Name"
                    fullWidth
                    variant="outlined"
                    value={editLastName}
                    onChange={(e) => setLastName(e.target.value)}
                    sx={{mb: 2}}
                />

                <FormControl fullWidth sx={{mb: 2}}>
                    <InputLabel>Roles</InputLabel>
                    <Select
                        multiple
                        value={editRoles}
                        label="Roles"
                        onChange={(e) => setRoles(e.target.value as string[])}
                        renderValue={(selected) => (
                            <div style={{display: "flex", flexWrap: "wrap", gap: 4}}>
                                {selected.map((value) => (
                                    <Chip key={value} label={value} size="small"/>
                                ))}
                            </div>
                        )}
                    >
                        {options?.roles.sort().map((role) => (
                            <MenuItem key={role} value={role}>{role}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <Button onClick={handleSave} variant="contained" disabled={saving}>
                    {saving ? "Saving..." : "Save"}
                </Button>
            </div>
        </div>
    );
}
